import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  GuiaBreadcrumb,
  ConversionBlock,
  PriceTag,
  GuiaFAQ,
  GuiaRelacionadas,
  SchemaArticle,
  SchemaFAQPage,
} from '../components/GuiaComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

const FAQS = [
  {
    question: '¿Por qué mi toldo no recoge del todo?',
    answer:
      'Las causas más frecuentes son: desgaste del muelle interior del brazo, acumulación de suciedad en el eje de enrollado, engranajes del mecanismo deteriorados, o un tensor mal ajustado. En toldos eléctricos, también puede deberse a un fallo en el motor o en los finales de carrera. Un diagnóstico profesional identifica la causa exacta en minutos.',
  },
  {
    question: '¿Puedo arreglar yo mismo un toldo que no recoge?',
    answer:
      'Tareas sencillas como limpiar el eje o ajustar ligeramente el tensor se pueden hacer con cuidado. Sin embargo, si el problema viene de un muelle roto o del engranaje interno, la reparación requiere desmontar el brazo, y el muelle acumula una tensión peligrosa que puede provocar lesiones graves. Para estos casos, lo más seguro es llamar a un profesional.',
  },
  {
    question: '¿Cuánto cuesta reparar un toldo que no recoge en Málaga?',
    answer:
      'Depende de la causa. Un ajuste de tensor o limpieza del mecanismo ronda los 50-70 €. Si hay que sustituir el muelle del brazo, el precio oscila entre 60 y 90 € por brazo. En toldos eléctricos, un cambio de final de carrera o reprogramación puede costar entre 70 y 110 €. Siempre damos presupuesto gratis antes de intervenir.',
  },
  {
    question: '¿El sol de Málaga afecta al mecanismo de recogida del toldo?',
    answer:
      'Sí, y mucho. Málaga tiene más de 300 días de sol al año, y la exposición continua a los rayos UV reseca las juntas, dilata las piezas metálicas y acelera el desgaste de los componentes internos del brazo. Además, el salitre del ambiente costero corroe los muelles y engranajes. Por eso recomendamos una revisión de mantenimiento al menos una vez al año.',
  },
];

export default function GuiaToldoNoRecoge() {
  return (
    <>
      <title>Mi toldo no recoge: causas y solución | Toldos Málaga</title>
      <meta
        name="description"
        content="¿Tu toldo no recoge? Descubre las causas más comunes y cómo solucionarlo. Reparación profesional de toldos en Málaga con respuesta 24/48h. Presupuesto gratis."
      />
      <meta
        name="keywords"
        content="toldo no recoge, toldo eléctrico no recoge, reparar toldo no recoge malaga, toldo no se enrolla"
      />
      <link rel="canonical" href="https://toldos-malaga.es/guias/toldo-no-recoge" />
      <SchemaArticle
        headline="Mi toldo no recoge: causas y solución"
        description="Guía completa sobre las causas por las que un toldo no recoge correctamente y las soluciones profesionales disponibles en Málaga. Incluye diagnóstico, precios orientativos y consejos de mantenimiento."
        datePublished="2025-11-20"
        dateModified="2026-06-16"
        imageUrl="/images/003-guia-toldo-no-recoge-og.webp"
        canonicalUrl="https://toldos-malaga.es/guias/toldo-no-recoge"
      />
      <SchemaFAQPage faqs={FAQS} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Reparación de toldo que no recoge en Málaga',
              description: 'Diagnóstico, ajuste de tensor, limpieza de mecanismo o cambio de muelle interior del brazo para toldos que no recogen correctamente.',
            },
            priceSpecification: {
              '@type': 'PriceSpecification',
              price: 60,
              priceCurrency: 'EUR',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <GuiaBreadcrumb currentTitle="Mi toldo no recoge: causas y solución" />

        {/* Hero Banner */}
        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/002-guia-toldo-no-recoge-hero.webp"
              alt="Toldo que no recoge correctamente en una terraza de Málaga"
              title="Problema de recogida en toldo - Reparación Málaga"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Guía de reparación
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Mi toldo no recoge: causas y solución
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        {/* Content */}
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              Llegas a casa, le das al mando o giras la manivela, y el toldo no responde como debería. Se queda a medio camino, no termina de enrollarse, o directamente ni se mueve. Es una de las averías más frustrantes, sobre todo en Málaga, donde el sol aprieta fuerte y una terraza sin sombra se convierte en un horno en cuestión de minutos. Vamos a ver por qué ocurre y qué puedes hacer.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Causas más comunes por las que un toldo no recoge
            </h2>

            <p>
              Un toldo es un mecanismo aparentemente sencillo, pero en su interior conviven muelles, engranajes, tensores y, en los modelos eléctricos, motores y sensores. Cualquier pequeño fallo en uno de estos elementos puede impedir que la lona se enrolle correctamente. Estas son las causas que más vemos en Málaga:
            </p>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">1</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Muelle del brazo fatigado o roto.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    El muelle interior del brazo es el encargado de generar la tensión necesaria para que el toldo se enrolle. Con los años —y en la costa malagueña, con el salitre acelerando la corrosión— el muelle pierde fuerza o se parte. Es la causa número uno en toldos de manivela.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">2</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Engranajes desgastados o bloqueados.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    El mecanismo de reducción que conecta la manivela o el motor con el eje de enrollado tiene engranajes que, al desgastarse, patinan o se traban. El polvo y la arena que trae el viento de levante se cuelan en el mecanismo y aceleran el deterioro.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">3</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Tensor desajustado.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Un tensor demasiado flojo no ejerce la presión suficiente para guiar la lona durante la recogida. Demasiado tenso, y el mecanismo fuerza de más. Ambos extremos provocan que el toldo no enrolle bien.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">4</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Suciedad acumulada en el eje.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Hojas, polvo, restos de insectos... todo se acumula en el tubo de enrollado y crea fricción. En Málaga, con la calima frecuente, este problema es especialmente común.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary-600 text-xs font-bold">5</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Fallo eléctrico (toldos motorizados).</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Finales de carrera desprogramados, condensador del motor quemado, o cableado dañado por la humedad. En la costa, la brisa marina carga de humedad las conexiones y acelera los fallos eléctricos.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Lo puedo arreglar yo mismo?
            </h2>

            <p>
              Vamos a ser sinceros: algunas cosas sí, otras definitivamente no. Si el problema es solo suciedad acumulada en el eje, puedes limpiarlo con un cepillo suave y un paño húmedo, siempre con el toldo completamente extendido y sin forzar ninguna pieza. También puedes comprobar visualmente si el tensor está muy flojo y darle un pequeño ajuste con la llave adecuada.
            </p>

            <p>
              Pero si el fallo viene del muelle interior del brazo o de los engranajes, la cosa cambia. Abrir un brazo de toldo sin la herramienta adecuada es peligroso: el muelle está sometido a una tensión enorme y puede soltarse de golpe, provocando cortes o lesiones en las manos. Lo mismo ocurre con los toldos eléctricos: manipular el cableado o el motor sin conocimientos puede provocar un cortocircuito o dañar aún más el sistema.
            </p>

            <p>
              Nuestra recomendación: si has comprobado la suciedad y el tensor y el problema persiste, <strong>no sigas forzando el mecanismo</strong>. Cada intento fallido de recogida desgasta más los engranajes y puede convertir una reparación sencilla en un cambio completo del brazo.
            </p>

            <div className="my-8">
              <ConversionBlock />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-8">
              <PriceTag price="desde 60 €" description="Reparación toldo no recoge" />
              <p className="text-sm text-foreground-500">
                Precio orientativo para una reparación estándar (ajuste de tensor, limpieza o cambio de muelle). El presupuesto exacto lo damos tras revisar tu toldo, sin compromiso.
              </p>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Preguntas frecuentes
            </h2>

            <GuiaFAQ faqs={FAQS} />

            <div className="mt-12">
              <GuiaRelacionadas />
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}